import clsx from 'clsx';
import React, { useMemo } from 'react';
import { RBAC_DISABLED_MESSAGE } from '../../../access-control/constants/strings';
import { RbacPermissions } from '../../../core/interfaces/user';
import { TRACKER_TARGET_CONFIG_POPUP_REAUTHORISE_CLICK } from '../constants';
import { Target } from '../models/target';
import { TARGET_TYPES, TargetTypesEnum } from '../models/target-type';
import { canReauthoriseOauthAccount, getReauthoriseOauthAccountParams, targetEditReauthRequired } from '../utils';
import { LegacySpinner } from '../../../loader/components/legacy-spinner/LegacySpinner';
import { ReAuthoriseNodeConfirmation } from '../../../connector/components/ReAuthoriseNodeConfirmation';
import { useReAuthoriseNode } from '../../../connector/hooks/useReAuthoriseNode';
import { HdIcon, HdIconButton, HdLink, HdTooltip } from '../../../components';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import useHasPermission from '../../../access-control/hooks/useHasPermission';
import useUserService from '../../../auth/hooks/useUserService';
import { EDIT_TARGET_QUERY_PARAM_TRIGGER, EditTargetDialog } from '../create/EditTargetDialog';
import { useEditEntityDialog } from '../../../core/hooks/useEditEntityDialog';

export interface TargetConfigPopupProps {
  iconClasses?: string;
  target: Target;
  showReauthoriseOption?: boolean;
  configPopupDisabled?: boolean;
}

export function TargetConfigPopup({
  iconClasses = '',
  target,
  showReauthoriseOption = true,
  configPopupDisabled = false
}: TargetConfigPopupProps) {
  const { eventTrack } = useAnalyticsTracker();

  const {
    reAuthorise,
    reAuthoriseNodeConfirmationData,
    onReAuthoriseNodeConfirmationClose
  } = useReAuthoriseNode();

  const reauthoriseTargetAccount = () => {
    eventTrack({
      action: TRACKER_TARGET_CONFIG_POPUP_REAUTHORISE_CLICK,
      properties: {
        targetId: target.id,
        targetType: target.targetType
      }
    });

    reAuthorise({
      ...getReauthoriseOauthAccountParams(target)
    });
  };

  return (
    <div className='center-flex-row'>
      <HdTooltip
        title={TargetConfigPopupContent}
        className='config-popup-react-popper'
        titleProps={{
          target,
          showReauthoriseOption,
          reauthoriseTargetAccount
        }}
        disabled={configPopupDisabled}
        mode='surface'
        placement='right-start'
        disableInteractive={false}
      >
        <HdIcon
          name={showReauthoriseOption ? 'settings' : 'info'}
          className={clsx('info-icon', iconClasses)}
        />
      </HdTooltip>

      <ReAuthoriseNodeConfirmation
        data={reAuthoriseNodeConfirmationData}
        onClose={onReAuthoriseNodeConfirmationClose}
      />

      <EditTargetDialog />
    </div>
  );
}

function TargetConfigPopupContent({
  target,
  showReauthoriseOption,
  reauthoriseTargetAccount,
  onClose
}: {
  target: Target;
  showReauthoriseOption: boolean;
  reauthoriseTargetAccount: Function;
  onClose: Function;
}) {
  const { user } = useUserService();
  const { hasPermission } = useHasPermission();
  const { navigate: navigateToEditTarget } = useEditEntityDialog({
    editEntityQueryParam: EDIT_TARGET_QUERY_PARAM_TRIGGER
  });

  const isRbacActionEnabled = hasPermission(RbacPermissions.ACTIVATION_EDIT);

  const canShowTargetEditOption = target.isEditable || TARGET_TYPES[target.targetType].isTargetConfigEditable;
  const editTargetReAuthRequired = targetEditReauthRequired(target, user);

  const hasDisplayConfigProperties = useMemo(() => {
    if (!target?.displayConfig) {
      return false;
    }

    return Object.keys(target.displayConfig)?.length > 0;
  }, [target]);

  const canReauthoriseTargetAccount = useMemo(() => {
    return canReauthoriseOauthAccount(target);
  }, []);

  const triggerTargetEdit = () => {
    navigateToEditTarget(target.seqId);
    onClose();
  };

  return (
    <div className={clsx('config-popup config-popup-react', !hasDisplayConfigProperties && 'pl-1 py-4')}>
      {!target ? (
        <LegacySpinner />
      ) : (
        <>
          {canShowTargetEditOption ? (
            <HdTooltip
              title={!isRbacActionEnabled ? RBAC_DISABLED_MESSAGE : 'Please reauthorize account in order to change settings'}
              disabled={!editTargetReAuthRequired && isRbacActionEnabled}>
              <div className='config-popup-action'>
                <HdIconButton
                  dataId='target-config-popup-edit'
                  verticalEdge='both'
                  horizontalEdge='both'
                  disabled={editTargetReAuthRequired || !isRbacActionEnabled}
                  onClick={() => triggerTargetEdit()}
                >
                  <HdIcon name='edit' size={2} />
                </HdIconButton>
              </div>
            </HdTooltip>
          ) : null}

          <div className={clsx('config-container', canShowTargetEditOption && 'mr-2')}>
            {Object.keys(target.displayConfig).map((key, index) => (
              <div
                key={index}
                className='config'
              >
                <span className='text-medium'>{key}</span>: {target.displayConfig[key]}
              </div>
            ))}
          </div>

          {showReauthoriseOption && canReauthoriseTargetAccount ? (
            <HdLink
              tag='button'
              className='mt-3'
              dataId='target-config-popup-reauthorise'
              onClick={() => {
                reauthoriseTargetAccount();
                onClose();
              }}
            >
              Reauthorize Account
            </HdLink>
          ) : null}
        </>
      )}
    </div>
  );
}
